import { useSeo } from "@/lib/seo";
import LegalLayout from "@/components/LegalLayout";

export default function PrivacyPolicy() {
    useSeo({
        title: "Politique de confidentialité — Coway",
        description:
            "Politique de confidentialité de l'application Coway. Comment nous collectons, utilisons et protégeons vos données personnelles.",
        canonical: "https://coway.fr/privacy-policy",
    });

    return (
        <LegalLayout
            kicker="Confidentialité"
            title="Politique de confidentialité"
            lastUpdated="1er février 2026"
            testId="privacy-policy-page"
        >
            <p>
                La présente politique de confidentialité décrit comment Coway
                (« nous », « notre », « nos ») collecte, utilise et protège vos
                données personnelles lorsque vous utilisez l'application mobile
                Coway et le site coway.fr (les « Services »).
            </p>

            <h2>1. Responsable du traitement</h2>
            <p>
                Le responsable du traitement des données est <strong>Coway</strong>,
                éditeur de l'application Coway, France. Pour toute question
                relative à vos données, contactez-nous à{" "}
                <a href="mailto:cowayfrance@gmail.com">cowayfrance@gmail.com</a>.
            </p>

            <h2>2. Données collectées</h2>
            <p>Nous collectons les catégories de données suivantes :</p>
            <ul>
                <li>
                    <strong>Données d'identité</strong> : prénom, nom, date de
                    naissance, numéro de téléphone, adresse email et pseudo
                    choisi par l'utilisateur.
                </li>
                <li>
                    <strong>Données de compte</strong> : identifiants de
                    connexion, préférences utilisateur.
                </li>
                <li>
                    <strong>Données de trajet</strong> : points de départ et
                    d'arrivée, horaires, historique des trajets partagés.
                </li>
                <li>
                    <strong>Données de communication</strong> : messages
                    échangés via le chat in-app entre passagers.
                </li>
                <li>
                    <strong>Données techniques</strong> : adresse IP, type
                    d'appareil, système d'exploitation, identifiant unique de
                    l'appareil, logs d'utilisation.
                </li>
            </ul>

            <p>
                Les informations de localisation sont utilisées uniquement
                pour permettre le fonctionnement des trajets et la mise en
                relation entre utilisateurs.
            </p>

            <p className="not-prose mt-6 p-4 rounded-2xl bg-coway-soft/40 border border-coway/20 text-sm text-slate-700">
                <strong className="text-slate-900">À noter :</strong> Coway ne
                traite aucun paiement et ne collecte donc aucune donnée
                bancaire ni de carte de crédit. L'organisation des frais de
                course se fait directement entre passagers, en dehors de
                l'application.
            </p>

            <h2>3. Finalités du traitement</h2>
            <p>Vos données sont utilisées pour :</p>
            <ul>
                <li>fournir et améliorer le service de mise en relation,</li>
                <li>vérifier votre identité et lutter contre la fraude,</li>
                <li>
                    faciliter la coordination des trajets et la communication
                    entre passagers,
                </li>
                <li>assurer la sécurité des trajets,</li>
                <li>vous envoyer des notifications relatives à vos trajets,</li>
                <li>respecter nos obligations légales.</li>
            </ul>

            <h2>4. Base légale</h2>
            <p>
                Les traitements reposent sur l'exécution du contrat (utilisation
                des Services), votre consentement (notifications marketing) et
                notre intérêt légitime (sécurité, prévention de la fraude).
            </p>

            <h2>5. Partage des données</h2>
            <p>
                Coway ne vend jamais vos données. Coway affiche uniquement le
                pseudo choisi par l'utilisateur aux autres passagers du
                trajet. Nous partageons par ailleurs des données uniquement
                avec :
            </p>
            <ul>
                <li>
                    nos sous-traitants (hébergement, notifications, analyse
                    technique),
                </li>
                <li>les autorités compétentes lorsque la loi l'exige.</li>
            </ul>

            <h2>6. Durée de conservation</h2>
            <p>
                Les données de compte sont conservées pendant toute la durée
                d'utilisation des Services puis archivées 3 ans à des fins de
                preuve. Les données de trajet sont conservées 5 ans à compter
                du dernier trajet à des fins de sécurité et de prévention de
                la fraude.
            </p>

            <h2>7. Vos droits</h2>
            <p>
                Conformément au RGPD, vous disposez des droits d'accès, de
                rectification, d'effacement, de limitation, de portabilité et
                d'opposition. Pour exercer ces droits, contactez-nous à{" "}
                <a href="mailto:cowayfrance@gmail.com">cowayfrance@gmail.com</a>.
                Vous pouvez également supprimer votre compte directement depuis
                l'application ou via{" "}
                <a href="/delete-account">/delete-account</a>.
            </p>

            <h2>8. Sécurité</h2>
            <p>
                Nous mettons en œuvre des mesures techniques et
                organisationnelles appropriées pour protéger vos données :
                chiffrement en transit (TLS), chiffrement au repos, contrôle
                d'accès strict, audits réguliers.
            </p>

            <h2>9. Cookies</h2>
            <p>
                Le site coway.fr utilise des cookies strictement nécessaires au
                fonctionnement et des cookies d'analyse anonymisés. Aucun
                cookie publicitaire n'est déposé sans votre consentement.
            </p>

            <h2>10. Mineurs</h2>
            <p>
                Coway est réservé aux personnes âgées d'au moins 18 ans. Nous
                ne collectons pas sciemment de données concernant des mineurs.
            </p>

            <h2>11. Modifications</h2>
            <p>
                Cette politique peut être mise à jour. Nous vous informerons de
                tout changement substantiel via l'application ou par email.
            </p>

            <h2>12. Contact</h2>
            <p>
                Pour toute question : <a href="mailto:cowayfrance@gmail.com">cowayfrance@gmail.com</a>.
                Vous pouvez également introduire une réclamation auprès de la
                CNIL (cnil.fr).
            </p>
        </LegalLayout>
    );
}
